import { create } from "zustand";
import { persist } from "zustand/middleware";
import { markSessionEnded } from "@/lib/auth-cookie";
import { stopBeams } from "@/lib/pusher-beams";
import type { RegisterSteps } from "@/types/user";

interface AuthState {
	isInitialized: boolean;
	isAuthenticated: boolean;
	rememberedEmail: string | null;
	pendingSteps: RegisterSteps | null;
	showLoginModal: boolean;
}

interface AuthActions {
	setInitialized: (initialized: boolean) => void;
	setAuthenticated: (authenticated: boolean) => void;
	setRememberedEmail: (email: string | null) => void;
	setPendingSteps: (steps: RegisterSteps | null) => void;
	setShowLoginModal: (show: boolean) => void;
	logout: (options?: { serverConfirmed?: boolean }) => void;
}

export const useAuthStore = create<AuthState & AuthActions>()(
	persist(
		(set) => ({
			isInitialized: false,
			isAuthenticated: false,
			rememberedEmail: null,
			pendingSteps: null,
			showLoginModal: false,

			setInitialized: (initialized) => set({ isInitialized: initialized }),
			setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),
			setRememberedEmail: (email) => set({ rememberedEmail: email }),
			setPendingSteps: (steps) => set({ pendingSteps: steps }),
			setShowLoginModal: (show) => set({ showLoginModal: show }),
			logout: (options) => {
				stopBeams();
				localStorage.removeItem("pusher-beams");
				// Marker auf "keine Session" setzen, nicht löschen: ein fehlendes
				// Cookie hieße "unbekannt", und die Session-Probe würde den Nutzer
				// nach einem fehlgeschlagenen Logout wieder einloggen.
				//
				// Ohne serverseitige Bestätigung wird das false persistent geschrieben:
				// Dann ist der Abmeldewunsch das Einzige, worauf wir uns stützen
				// können, und er muss den Browserstart überleben. Default ist bewusst
				// die vorsichtige Variante.
				markSessionEnded({ persist: options?.serverConfirmed !== true });
				set({
					isAuthenticated: false,
					pendingSteps: null,
					showLoginModal: false,
					// rememberedEmail bewusst beibehalten (Pre-Fill bei erneutem Login)
				});
			},
		}),
		{
			name: "auth-store",
			// Nur rememberedEmail persistieren, nicht transiente Flags
			partialize: (state) => ({
				rememberedEmail: state.rememberedEmail,
			}),
		},
	),
);
